import type { ChartWidget as ChartWidgetSpec, PageRow } from '@developerz/domain';
import { createMemo, For, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { barHeightPct, chartModel } from '../../lib/chart';
import { formatCell } from '../../lib/widget-format';

// `chart` presenter (arc 101 slice 09) — a dependency-free CSS column chart:
// series grouped under each x-axis label, heights normalized to the model max.
// The visual is `aria-hidden`; an sr-only data table is the text alternative, so
// the chart is accessible without a charting runtime. Empty / all-zero → empty
// state. Richer typed (true line/area/pie) charts can layer on later.
export function ChartWidget(props: {
  spec: ChartWidgetSpec;
  rows: readonly PageRow[];
}): JSX.Element {
  const model = createMemo(() => chartModel(props.spec, props.rows));
  return (
    <Show when={model().hasData} fallback={<p class="widget__empty">{t('widgets.empty')}</p>}>
      <figure class="widget-chart">
        <div class="bar-chart" aria-hidden="true">
          <For each={model().labels}>
            {(label, i) => (
              <div class="widget-chart__group">
                <div class="widget-chart__bars">
                  <For each={model().series}>
                    {(s) => (
                      <span
                        class="bar-chart__bar"
                        style={{
                          height: `${barHeightPct(s.points[i()]?.value ?? 0, model().max)}%`,
                        }}
                        title={`${s.label}: ${s.points[i()]?.value ?? 0}`}
                      />
                    )}
                  </For>
                </div>
                <span class="widget-chart__label">{label}</span>
              </div>
            )}
          </For>
        </div>
        <figcaption class="sr-only">
          <table>
            <caption>{t('widgets.chart_alt')}</caption>
            <thead>
              <tr>
                <th scope="col">{props.spec.x}</th>
                <For each={model().series}>{(s) => <th scope="col">{s.label}</th>}</For>
              </tr>
            </thead>
            <tbody>
              <For each={model().labels}>
                {(label, i) => (
                  <tr>
                    <td>{label}</td>
                    <For each={model().series}>
                      {(s) => <td>{formatCell(s.points[i()]?.value, 'number')}</td>}
                    </For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </figcaption>
      </figure>
    </Show>
  );
}
